import React, { useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import ProgrammePage from './ProgrammePage';
import { SidebarContext } from '../../../../context/SidebarContext';
import { useCompanySectionFilter } from '../../../../component/B2B/CompanySectionLayout';
import { fetchCareProgramme } from './companyCareApi';
import { PROGRAMME_BY_PATH, PROGRAMME_LABELS, SECTION_PROGRAMMES } from './careTheme';

/**
 * Route wrapper for one programme's page. Every programme route in both
 * sections renders this; the programme is the last segment of the path, so six
 * near-identical wrappers would have said nothing the URL doesn't already.
 *
 * THE SECTION IS NOT TAKEN FROM THE URL SEGMENT. It is fixed by which route
 * tree rendered this component, and a segment that does not belong to that
 * section falls back to the section's first programme rather than being passed
 * through. The backend rejects a cross-section request anyway; this stops the
 * page from asking in the first place.
 */
const CProgrammePage = ({ section }) => {
  const location = useLocation();
  const { isB2BFansMode } = useContext(SidebarContext);
  const { department } = useCompanySectionFilter();

  const allowed = SECTION_PROGRAMMES[section] || [];
  const segment = location.pathname.split('/').filter(Boolean).pop();
  const candidate = PROGRAMME_BY_PATH[segment];
  const programme = allowed.includes(candidate) ? candidate : allowed[0];

  const fetcher = useCallback(
    () => fetchCareProgramme(section, programme, department),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [section, programme, isB2BFansMode, department]
  );

  return (
    <div>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{PROGRAMME_LABELS[programme] || 'Care'} | Shoorah</title>
      </Helmet>
      <ProgrammePage fetcher={fetcher} populationLabel={isB2BFansMode ? 'members' : 'employees'} />
    </div>
  );
};

CProgrammePage.propTypes = {
  section: PropTypes.oneOf(['care', 'reproductive']).isRequired
};

export const CCareProgramme = () => <CProgrammePage section="care" />;
export const CReproductiveProgramme = () => <CProgrammePage section="reproductive" />;

export default CProgrammePage;
